/**
 * React component registry for the Taqat json-render catalog.
 * Maps every catalog entry to a concrete React implementation.
 *
 * shadcn entries delegate to @json-render/shadcn implementations.
 * Custom domain entries are extracted from the existing renderer components.
 *
 * @module lib/json-render/registry
 */

'use client'

import { useState, useCallback, useRef } from 'react'
import { defineRegistry, type BaseComponentProps } from '@json-render/react'
import { shadcnComponents } from '@json-render/shadcn'
import { getLegalString } from './legal-strings'
import { getEmbedLocale } from '@/lib/embed-locale'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertTriangle,
  Check,
  CheckCircle,
  CheckCircle2,
  Circle,
  FileText,
  Globe,
  Info,
  Lightbulb,
  Loader2,
  Mail,
  MessageSquare,
  Slack,
  Upload,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Markdown } from '@/components/ui/markdown'
import { InteractiveIcon } from '@/components/chat/interactive/shared/interactive-icon'
import {
  deleteFile,
  uploadOnboardingFiles,
  type OnboardingUploadedFile,
} from '@/services/upload-service'
import {
  DetailedAttachmentList,
  parseAttachments,
} from '@/components/chat/messages/tool-call-message/components/attachment-list'
import { taqatCatalog } from './catalog'
import { useChatRenderer } from './chat-context'

// ── Status config maps (from existing renderers) ──────────

const FINDING_STATUS_CONFIG = {
  compliant: { label: 'Compliant', badge: 'default' as const, border: 'border-l-green-500', bg: 'bg-green-500/5' },
  partial: { label: 'Partial', badge: 'secondary' as const, border: 'border-l-orange-500', bg: 'bg-orange-500/5' },
  'non-compliant': { label: 'Non-Compliant', badge: 'destructive' as const, border: 'border-l-red-500', bg: 'bg-red-500/5' },
}

const FINDING_PRIORITY_CONFIG = {
  high: { label: 'High', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  medium: { label: 'Medium', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  low: { label: 'Low', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
}

const SECTION_STATUS_ICON: Record<string, React.ReactNode> = {
  complete: <CheckCircle2 className="size-4 text-green-500" />,
  'in-progress': <Loader2 className="size-4 text-primary animate-spin" />,
  pending: <Circle className="size-4 text-muted-foreground/40" />,
}

const SCORE_COLOR_MAP: Record<string, string> = {
  success: 'bg-green-500',
  warning: 'bg-orange-500',
  destructive: 'bg-red-500',
}

const SCORE_TEXT_COLOR_MAP: Record<string, string> = {
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-orange-600 dark:text-orange-400',
  destructive: 'text-red-600 dark:text-red-400',
}

const MESSAGE_TYPE_CONFIG = {
  greeting: { className: 'text-center py-4', textClassName: 'text-base font-medium', icon: null, borderClassName: '' },
  info: { className: 'p-3', textClassName: 'text-sm', icon: Info, borderClassName: 'border-border/50' },
  success: { className: 'p-3 border-l-4 border-l-green-500', textClassName: 'text-sm', icon: CheckCircle2, borderClassName: 'border-border/50' },
  warning: { className: 'p-3 border-l-4 border-l-amber-500', textClassName: 'text-sm', icon: AlertTriangle, borderClassName: 'border-border/50' },
  transition: { className: 'p-3 bg-muted/50', textClassName: 'text-sm font-semibold', icon: null, borderClassName: 'border-border/30' },
} as const

const CHANNEL_ICONS: Record<string, React.ElementType> = { email: Mail, whatsapp: MessageSquare, slack: Slack, web: Globe }
const CHANNEL_LABELS: Record<string, string> = { email: 'Email', whatsapp: 'WhatsApp', slack: 'Slack', web: 'Web App (Dashboard)' }
const CHANNEL_DESCRIPTIONS: Record<string, string> = {
  email: 'Receive updates and reports via email',
  whatsapp: 'Get alerts and interact via WhatsApp',
  slack: 'Connect to a Slack channel for notifications',
  web: 'View everything in the Taqat dashboard',
}

/** Generate milestone dates working backwards from target */
function generateMilestones(targetDateStr: string) {
  const target = new Date(targetDateStr)
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  const milestones = [
    { label: 'Gap Analysis', offset: -7 },
    { label: 'Document Development', offset: -6 },
    { label: 'Implementation', offset: -5 },
    { label: 'Internal Audit', offset: -3 },
    { label: 'Management Review', offset: -2 },
    { label: 'Certification Audit', offset: 0 },
  ]
  return milestones.map((m) => {
    const d = new Date(target)
    d.setMonth(d.getMonth() + m.offset)
    return { label: m.label, date: fmt(d) }
  })
}

// ── Registry definition ───────────────────────────────────

export const { registry, handlers } = defineRegistry(taqatCatalog, {
  components: {
    // ── shadcn/ui delegated ─────────────────────────────
    Card: shadcnComponents.Card,
    Stack: shadcnComponents.Stack,
    Grid: shadcnComponents.Grid,
    Separator: shadcnComponents.Separator,
    Tabs: shadcnComponents.Tabs,
    Accordion: shadcnComponents.Accordion,
    Heading: shadcnComponents.Heading,
    Text: shadcnComponents.Text,
    Badge: shadcnComponents.Badge,
    Alert: shadcnComponents.Alert,
    Progress: shadcnComponents.Progress,
    Avatar: shadcnComponents.Avatar,
    Image: shadcnComponents.Image,
    Input: shadcnComponents.Input,
    Textarea: shadcnComponents.Textarea,
    Select: shadcnComponents.Select,
    Checkbox: shadcnComponents.Checkbox,
    Radio: shadcnComponents.Radio,
    Switch: shadcnComponents.Switch,
    Slider: shadcnComponents.Slider,
    Button: shadcnComponents.Button,
    ButtonGroup: shadcnComponents.ButtonGroup,

    // ── AttachmentList (reuses DetailedAttachmentList) ────
    AttachmentList: ({ props }: BaseComponentProps) => {
      const p = props as { attachments: string[] }
      const { sandboxId } = useChatRenderer()
      const attachments = parseAttachments(p.attachments)
      if (attachments.length === 0) return null
      return <DetailedAttachmentList attachments={attachments} sandboxId={sandboxId} />
    },

    // ── FindingCard (from finding-renderer.tsx) ──────────
    FindingCard: ({ props }: BaseComponentProps) => {
      const p = props as {
        title: string; status: 'compliant' | 'partial' | 'non-compliant'
        control: string; priority: 'high' | 'medium' | 'low'
        effort?: string | null; description: string; recommendation: string
      }
      const statusCfg = FINDING_STATUS_CONFIG[p.status] || FINDING_STATUS_CONFIG.partial
      const priorityCfg = FINDING_PRIORITY_CONFIG[p.priority] || FINDING_PRIORITY_CONFIG.medium
      return (
        <Card className={cn('border-l-4 overflow-hidden', statusCfg.border)}>
          <CardHeader className={cn('pb-2 pt-3 px-4', statusCfg.bg)}>
            <div className="flex items-center justify-between gap-2">
              <Badge variant={statusCfg.badge} className="text-xs">{statusCfg.label}</Badge>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn('text-xs', priorityCfg.className)}>{priorityCfg.label}</Badge>
                {p.effort && <span className="text-xs text-muted-foreground">{p.effort}</span>}
              </div>
            </div>
            <h4 className="text-sm font-semibold mt-1">{p.title}</h4>
          </CardHeader>
          <CardContent className="px-4 pb-3 pt-2 space-y-2">
            <p className="text-sm text-muted-foreground">{p.description}</p>
            <div className="text-xs text-muted-foreground">
              Control: <span className="font-mono font-medium">{p.control}</span>
            </div>
            <Separator />
            <div className="flex gap-2 items-start">
              <Lightbulb className="size-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm">{p.recommendation}</p>
            </div>
          </CardContent>
        </Card>
      )
    },

    // ── SectionProgress (from progress-renderer.tsx) ────
    SectionProgress: ({ props }: BaseComponentProps) => {
      const p = props as {
        title: string; overall: number; currentSection?: string | null
        currentQuestion?: number | null; totalQuestions?: number | null
        sections: Array<{ name: string; status: 'complete' | 'in-progress' | 'pending'; score: string }>
      }
      const sections = p.sections || []
      return (
        <Card>
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">{p.title}</h4>
              <span className="text-sm font-bold text-primary">{Math.round(p.overall)}%</span>
            </div>
            <Progress value={p.overall} className="h-2 mt-1" />
            {p.currentSection && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {p.currentSection}
                {p.currentQuestion && p.totalQuestions ? ` · Question ${p.currentQuestion} of ${p.totalQuestions}` : ''}
              </p>
            )}
          </CardHeader>
          {sections.length > 0 && (
            <CardContent className="px-4 pb-3 pt-1">
              <div className="space-y-1.5">
                {sections.map((section, index) => (
                  <div key={index} className={cn('flex items-center gap-2 py-1 px-2 rounded text-sm', section.status === 'in-progress' && 'bg-muted')}>
                    {SECTION_STATUS_ICON[section.status]}
                    <span className={cn('flex-1', section.status === 'pending' && 'text-muted-foreground', section.status === 'in-progress' && 'font-medium')}>
                      {section.name}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">{section.score}</span>
                    {section.status === 'in-progress' && <span className="text-xs text-primary">&lArr;</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )
    },

    // ── Scorecard (from scorecard-renderer.tsx) ──────────
    Scorecard: ({ props }: BaseComponentProps) => {
      const p = props as {
        title: string; score: number; maxScore?: number | null
        breakdown: Array<{ label: string; value: number; color: 'success' | 'warning' | 'destructive' }>
        actions?: Array<{ label: string; primary?: boolean | null }> | null
      }
      const { isLastMessage, submittedText, isInteractive, sendMessage } = useChatRenderer()
      const maxScore = p.maxScore ?? 100
      const breakdown = p.breakdown || []
      const actions = p.actions || []
      const restoredIndex = !isLastMessage && submittedText && actions.length > 0
        ? actions.findIndex((a) => a.label === submittedText.trim()) : -1
      const [selectedAction, setSelectedAction] = useState<number | null>(restoredIndex >= 0 ? restoredIndex : null)
      const percentage = Math.round((p.score / maxScore) * 100)

      const handleActionClick = (index: number) => {
        if (!isInteractive || selectedAction !== null) return
        setSelectedAction(index)
        sendMessage?.(actions[index].label)
      }

      return (
        <Card>
          <CardHeader className="pb-2 pt-3 px-4 text-center">
            <h4 className="text-sm font-semibold">{p.title}</h4>
            <div className="text-3xl font-bold text-primary mt-2">{percentage}%</div>
            <Progress value={percentage} className="h-2 mt-2" />
          </CardHeader>
          {breakdown.length > 0 && (
            <CardContent className="px-4 pb-3 pt-1">
              <div className="space-y-1.5">
                {breakdown.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className={cn('size-2.5 rounded-full shrink-0', SCORE_COLOR_MAP[item.color])} />
                    <span className="text-sm flex-1">{item.label}</span>
                    <span className={cn('text-sm font-semibold', SCORE_TEXT_COLOR_MAP[item.color])}>{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
          {actions.length > 0 && (
            <>
              <Separator />
              <div className="p-3 flex flex-wrap gap-2">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.primary ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1 min-w-[120px]"
                    disabled={!isInteractive || (selectedAction !== null && selectedAction !== index)}
                    onClick={() => handleActionClick(index)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </>
          )}
        </Card>
      )
    },

    // ── SummaryGrid (from summary-renderer.tsx) ──────────
    SummaryGrid: ({ props }: BaseComponentProps) => {
      const p = props as {
        title: string; items: Array<{ label: string; value: string }>; footer?: string | null
      }
      const items = p.items || []
      return (
        <Card>
          <CardHeader className="pb-2 pt-3 px-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{p.title}</h4>
          </CardHeader>
          <Separator />
          <CardContent className="px-4 py-3">
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              {items.map((item, index) => (
                <div key={index} className="contents">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{item.label}</span>
                  <span className="text-sm font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
          {p.footer && (
            <>
              <Separator />
              <CardFooter className="px-4 py-2.5">
                <p className="text-sm text-muted-foreground">{p.footer}</p>
              </CardFooter>
            </>
          )}
        </Card>
      )
    },

    // ── StyledMessage (from message-renderer.tsx) ────────
    StyledMessage: ({ props }: BaseComponentProps) => {
      const p = props as { text: string; type: 'greeting' | 'info' | 'success' | 'warning' | 'transition' }
      const type = p.type || 'info'
      const config = MESSAGE_TYPE_CONFIG[type]
      const IconComponent = config.icon

      if (type === 'greeting') {
        return (
          <Card className={cn('border-border/50', config.className)}>
            <div className="flex items-center justify-center gap-2">
              <MessageSquare className="size-5 text-primary" />
              <div className={config.textClassName}><Markdown>{p.text}</Markdown></div>
            </div>
          </Card>
        )
      }

      if (type === 'transition') {
        return (
          <Card className={cn(config.borderClassName, config.className)}>
            <div className={config.textClassName}><Markdown>{p.text}</Markdown></div>
          </Card>
        )
      }

      return (
        <Card className={cn(config.borderClassName, config.className)}>
          <div className="flex items-start gap-2">
            {IconComponent && (
              <IconComponent className={cn(
                'size-4 shrink-0 mt-0.5',
                type === 'success' && 'text-green-500',
                type === 'warning' && 'text-amber-500',
                type === 'info' && 'text-muted-foreground',
              )} />
            )}
            <div className={config.textClassName}><Markdown>{p.text}</Markdown></div>
          </div>
        </Card>
      )
    },

    // ── OptionsList (from options-renderer.tsx) ──────────
    OptionsList: ({ props }: BaseComponentProps) => {
      const p = props as {
        options: Array<{ label: string; description?: string | null; icon?: string | null }>
        allowFreeText?: boolean | null
      }
      const { isLastMessage, submittedText, isInteractive, sendMessage } = useChatRenderer()
      const options = p.options || []
      const restoredIndex = !isLastMessage && submittedText
        ? options.findIndex((o) => o.label === submittedText.trim()) : -1
      const [selected, setSelected] = useState<number | null>(restoredIndex >= 0 ? restoredIndex : null)

      const handleClick = (index: number) => {
        if (!isInteractive || selected !== null) return
        setSelected(index)
        sendMessage?.(options[index].label)
      }

      return (
        <Card className="p-3 space-y-2 border-border/50">
          {options.map((option, index) => (
            <Button
              key={index}
              variant="outline"
              className={cn(
                'w-full justify-start text-left h-auto py-2.5 px-3 gap-3',
                selected === index && 'border-primary bg-primary/5',
                selected !== null && selected !== index && 'opacity-50',
              )}
              disabled={!isInteractive || (selected !== null && selected !== index)}
              onClick={() => handleClick(index)}
            >
              <div className="flex items-start gap-3 w-full">
                <div className="shrink-0 mt-0.5">
                  {selected === index ? (
                    <Check className="size-4 text-primary" />
                  ) : (
                    <InteractiveIcon name={option.icon ?? undefined} className="size-4 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{option.label}</div>
                  {option.description && <div className="text-xs text-muted-foreground mt-0.5">{option.description}</div>}
                </div>
              </div>
            </Button>
          ))}
          {p.allowFreeText && selected === null && isInteractive && (
            <p className="text-xs text-muted-foreground text-center pt-1">Or type your own answer below</p>
          )}
        </Card>
      )
    },

    // ── ActionButtons (from actions-renderer.tsx) ────────
    ActionButtons: ({ props }: BaseComponentProps) => {
      const p = props as { actions: Array<{ label: string; primary?: boolean | null }> }
      const { isLastMessage, submittedText, isInteractive, sendMessage } = useChatRenderer()
      const actions = p.actions || []
      const restoredIndex = !isLastMessage && submittedText
        ? actions.findIndex((a) => a.label === submittedText.trim()) : -1
      const [selected, setSelected] = useState<number | null>(restoredIndex >= 0 ? restoredIndex : null)

      const handleClick = (index: number) => {
        if (!isInteractive || selected !== null) return
        setSelected(index)
        sendMessage?.(actions[index].label)
      }

      return (
        <div className="flex flex-wrap gap-2 py-1">
          {actions.map((action, index) => (
            <Button
              key={index}
              variant={action.primary ? 'default' : 'outline'}
              size="sm"
              className={cn(
                selected === index && 'border-primary bg-primary/10',
                selected !== null && selected !== index && 'opacity-50',
              )}
              disabled={!isInteractive || (selected !== null && selected !== index)}
              onClick={() => handleClick(index)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )
    },

    // ── FileUploadZone (from file-upload-step.tsx) ───────
    FileUploadZone: ({ props, emit }: BaseComponentProps) => {
      const p = props as { accept?: string[] | null; maxSize?: string | null; hints?: string[] | null }
      const inputRef = useRef<HTMLInputElement>(null)
      const [files, setFiles] = useState<OnboardingUploadedFile[]>([])
      const [isUploading, setIsUploading] = useState(false)
      const [uploadError, setUploadError] = useState<string | null>(null)

      const handleFiles = useCallback(async (fileList: FileList | null) => {
        if (!fileList) return
        const newFiles = Array.from(fileList)
        if (newFiles.length === 0) return

        setIsUploading(true)
        setUploadError(null)

        try {
          const uploaded = await uploadOnboardingFiles(newFiles)
          setFiles((prev) => {
            const nextFiles = [...prev, ...uploaded]
            emit('upload', { files: nextFiles })
            return nextFiles
          })
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to upload files'
          setUploadError(message)
        } finally {
          setIsUploading(false)
        }
      }, [emit])

      const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        void handleFiles(e.dataTransfer.files)
      }, [handleFiles])

      const handleRemove = async (index: number) => {
        const fileToRemove = files[index]
        if (!fileToRemove) return

        const result = await deleteFile(fileToRemove.s3Url)
        if (!result.success) {
          setUploadError(result.error || 'Failed to remove file from storage')
          return
        }

        setUploadError(null)
        setFiles((prev) => {
          const nextFiles = prev.filter((_, i) => i !== index)
          emit('upload', { files: nextFiles })
          return nextFiles
        })
      }

      const acceptString = p.accept?.join(',') || '*'

      return (
        <div className="space-y-4">
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
            className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 cursor-pointer transition-colors hover:border-primary/50 hover:bg-muted/30"
          >
            <Upload className="size-8 text-muted-foreground" />
            <div className="text-center">
              <p className="text-sm font-medium">Drag & drop files here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">
                {p.accept?.join(', ') || 'All file types'} {p.maxSize && `(Max ${p.maxSize})`}
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              name="files"
              multiple
              accept={acceptString}
              onChange={(e) => {
                void handleFiles(e.target.files)
                e.target.value = ''
              }}
              className="hidden"
            />
          </div>
          <input
            type="hidden"
            name="files"
            value={JSON.stringify(files)}
          />
          {isUploading && (
            <p className="text-sm text-muted-foreground">Uploading files...</p>
          )}
          {uploadError && (
            <p className="text-sm text-destructive">{uploadError}</p>
          )}
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file, index) => (
                <div key={`${file.id}-${index}`} className="flex items-center gap-3 rounded-lg border p-3">
                  <FileText className="size-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.originalName}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 flex-shrink-0"
                    disabled={isUploading}
                    onClick={() => void handleRemove(index)}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {p.hints && p.hints.length > 0 && (
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm font-medium mb-2">Common documents to upload:</p>
              <ul className="space-y-1">
                {p.hints.map((hint) => (
                  <li key={hint} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-muted-foreground/50" />
                    {hint}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-3">Don't have these? No problem. We'll generate what you need.</p>
            </div>
          )}
        </div>
      )
    },

    // ── ChannelSelector (from channel-select-step.tsx) ───
    ChannelSelector: ({ props, emit }: BaseComponentProps) => {
      const p = props as { channels: string[] }
      const channels = p.channels || ['email', 'web']
      const [value, setValue] = useState<Record<string, { enabled: boolean; config?: string }>>({})

      const handleToggle = (channel: string, enabled: boolean) => {
        setValue((prev) => {
          const nextValue = { ...prev, [channel]: { ...prev[channel], enabled } }
          emit('change', { data: nextValue })
          return nextValue
        })
      }

      const handleConfigChange = (channel: string, configValue: string) => {
        setValue((prev) => {
          const nextValue = {
            ...prev,
            [channel]: { ...prev[channel], enabled: true, config: configValue },
          }
          emit('change', { data: nextValue })
          return nextValue
        })
      }

      return (
        <div className="space-y-3">
          <input type="hidden" name="channels" value={JSON.stringify(value)} />
          {channels.map((channel) => {
            const Icon = CHANNEL_ICONS[channel] || Globe
            const isEnabled = value[channel]?.enabled ?? channel === 'web'
            return (
              <div key={channel} className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-muted">
                      <Icon className="size-4" />
                    </div>
                    <div>
                      <Label className="font-medium">{CHANNEL_LABELS[channel] || channel}</Label>
                      <p className="text-xs text-muted-foreground">{CHANNEL_DESCRIPTIONS[channel] || ''}</p>
                    </div>
                  </div>
                  <Switch checked={isEnabled} onCheckedChange={(checked) => handleToggle(channel, checked)} />
                </div>
                {isEnabled && (channel === 'email' || channel === 'whatsapp') && (
                  <Input
                    placeholder={channel === 'email' ? 'email@example.com' : '+974 5555 1234'}
                    value={value[channel]?.config || ''}
                    onChange={(e) => handleConfigChange(channel, e.target.value)}
                    className="mt-2"
                  />
                )}
                {isEnabled && channel === 'slack' && (
                  <Input
                    placeholder="#compliance-channel"
                    value={value[channel]?.config || ''}
                    onChange={(e) => handleConfigChange(channel, e.target.value)}
                    className="mt-2"
                  />
                )}
              </div>
            )
          })}
        </div>
      )
    },

    // ── TimelineView (from timeline-step.tsx) ────────────
    TimelineView: ({ props, emit }: BaseComponentProps) => {
      const p = props as {
        fields?: Array<{
          name: string; label: string
          type: 'text' | 'date' | 'select' | 'email' | 'number'
          required?: boolean | null; placeholder?: string | null
          options?: Array<{ value: string; label: string }> | null
        }> | null
        autoGenerate?: boolean | null
      }
      const [value, setValue] = useState<Record<string, string>>({})
      const fields = p.fields || []

      const handleFieldChange = (fieldName: string, fieldValue: string) => {
        setValue((prev) => {
          const nextValue = { ...prev, [fieldName]: fieldValue }
          emit('change', { data: nextValue })
          return nextValue
        })
      }

      return (
        <div className="space-y-6">
          {fields.length > 0 && (
            <div className="space-y-4">
              {fields.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  {field.type === 'date' ? (
                    <Input
                      id={field.name}
                      name={field.name}
                      type="date"
                      value={value[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    />
                  ) : field.type === 'select' ? (
                    <>
                      <input type="hidden" name={field.name} value={value[field.name] || ''} />
                      <Select value={value[field.name] || ''} onValueChange={(v) => handleFieldChange(field.name, v)}>
                      <SelectTrigger><SelectValue placeholder={field.placeholder || 'Select...'} /></SelectTrigger>
                      <SelectContent>
                        {field.options?.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    </>
                  ) : (
                    <Input
                      id={field.name}
                      name={field.name}
                      type={field.type}
                      placeholder={field.placeholder ?? undefined}
                      value={value[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          {p.autoGenerate && value.targetDate && (
            <div className="rounded-lg border p-4">
              <p className="text-sm font-medium mb-3">Generated Timeline</p>
              <div className="space-y-3">
                {generateMilestones(value.targetDate).map((milestone, idx) => (
                  <div key={milestone.label} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className="flex size-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <CheckCircle className="size-3.5" />
                      </div>
                      {idx < 5 && <div className="w-px h-6 bg-border mt-1" />}
                    </div>
                    <div className="flex-1 pb-1">
                      <p className="text-sm font-medium">{milestone.label}</p>
                      <p className="text-xs text-muted-foreground">{milestone.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    },

    // ── StatCards (dashboard KPI row) ─────────────────────
    StatCards: ({ props }: BaseComponentProps) => {
      const p = props as {
        cards: Array<{
          label: string; value: string | number
          change?: string | null; changeType?: 'positive' | 'negative' | 'neutral' | null
          icon?: string | null
        }>
      }
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(p.cards || []).map((card, i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{card.label}</span>
                {card.icon && <InteractiveIcon name={card.icon} className="size-4 text-muted-foreground" />}
              </div>
              <div className="text-2xl font-bold">{card.value}</div>
              {card.change && (
                <span className={cn('text-xs font-medium mt-1',
                  card.changeType === 'positive' && 'text-green-600 dark:text-green-400',
                  card.changeType === 'negative' && 'text-red-600 dark:text-red-400',
                  (!card.changeType || card.changeType === 'neutral') && 'text-muted-foreground'
                )}>
                  {card.change}
                </span>
              )}
            </Card>
          ))}
        </div>
      )
    },

    // ── DataTable (dashboard sortable table) ────────────
    DataTable: ({ props }: BaseComponentProps) => {
      const p = props as {
        columns: Array<{ key: string; label: string }>
        rows: Array<Record<string, unknown>>
        emptyMessage?: string | null
      }
      const columns = p.columns || []
      const rows = p.rows || []

      if (rows.length === 0) {
        return (
          <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
            {p.emptyMessage || 'No data available'}
          </div>
        )
      }

      return (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                {columns.map((col) => (
                  <th key={col.key} className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-2.5">
                      {String(row[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    },

    // ── Checklist (dashboard task list) ──────────────────
    Checklist: ({ props }: BaseComponentProps) => {
      const p = props as {
        title: string
        items: Array<{ id: string; label: string; checked: boolean; description?: string | null }>
      }
      return (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="text-sm font-semibold">{p.title}</h3>
          </CardHeader>
          <CardContent className="space-y-2">
            {(p.items || []).map((item) => (
              <div key={item.id} className="flex items-start gap-3 py-1">
                <div className={cn('mt-0.5 flex size-5 items-center justify-center rounded-full shrink-0',
                  item.checked ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'
                )}>
                  {item.checked
                    ? <Check className="size-3 text-green-600 dark:text-green-400" />
                    : <Circle className="size-3 text-muted-foreground/40" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <span className={cn('text-sm', item.checked && 'line-through text-muted-foreground')}>
                    {item.label}
                  </span>
                  {item.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )
    },

    // ── ProgressBar (labeled progress indicator) ────────
    ProgressBar: ({ props }: BaseComponentProps) => {
      const p = props as { label: string; value: number; size?: 'sm' | 'md' | 'lg' | null }
      const height = p.size === 'lg' ? 'h-4' : p.size === 'sm' ? 'h-1.5' : 'h-2.5'
      return (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{p.label}</span>
            <span className="text-sm text-muted-foreground">{p.value}%</span>
          </div>
          <div className={cn('w-full rounded-full bg-muted overflow-hidden', height)}>
            <div
              className={cn('h-full rounded-full bg-primary transition-all', height)}
              style={{ width: `${Math.min(100, Math.max(0, p.value))}%` }}
            />
          </div>
        </div>
      )
    },

    // ── ActivityFeed (recent activity timeline) ─────────
    ActivityFeed: ({ props }: BaseComponentProps) => {
      const p = props as {
        items: Array<{
          id: string; title?: string | null; action?: string | null
          description?: string | null; details?: string | null
          timestamp: string; type?: 'info' | 'success' | 'warning' | 'error' | null
        }>
      }
      const typeIcon: Record<string, React.ReactNode> = {
        success: <CheckCircle2 className="size-3.5 text-green-500" />,
        warning: <AlertTriangle className="size-3.5 text-amber-500" />,
        error: <X className="size-3.5 text-red-500" />,
        info: <Info className="size-3.5 text-blue-500" />,
      }
      const formatTime = (ts: string) => {
        const d = new Date(ts)
        if (Number.isNaN(d.getTime())) return ts
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      }
      return (
        <div className="space-y-0">
          {(p.items || []).map((item, i) => {
            const title = item.title || item.action || 'Activity'
            const description = item.description || item.details
            return (
              <div key={item.id} className="flex gap-3 py-2.5">
                <div className="flex flex-col items-center">
                  <div className="flex size-6 items-center justify-center rounded-full bg-muted">
                    {typeIcon[item.type || 'info'] || typeIcon.info}
                  </div>
                  {i < (p.items || []).length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                </div>
                <div className="flex-1 min-w-0 pb-1">
                  <p className="text-sm font-medium">{title}</p>
                  {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
                  <p className="text-[11px] text-muted-foreground mt-1">{formatTime(item.timestamp)}</p>
                </div>
              </div>
            )
          })}
        </div>
      )
    },

    // ── EmptyState (placeholder with CTA) ───────────────
    EmptyState: ({ props }: BaseComponentProps) => {
      const p = props as {
        title: string; description?: string | null
        icon?: string | null; action?: { label: string } | null
      }
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          {p.icon && (
            <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
              <InteractiveIcon name={p.icon} className="size-6 text-muted-foreground" />
            </div>
          )}
          <h3 className="text-base font-semibold">{p.title}</h3>
          {p.description && <p className="mt-1 text-sm text-muted-foreground max-w-xs">{p.description}</p>}
          {p.action && (
            <Button variant="outline" size="sm" className="mt-4">{p.action.label}</Button>
          )}
        </div>
      )
    },

    // ── LegalText (frozen, pre-approved consent copy) ────
    LegalText: ({ props }: BaseComponentProps) => {
      const p = props as { key: string; locale?: string | null }
      // Session locale wins when the spec doesn't pin one (embed surface).
      const text = getLegalString(p.key, p.locale ?? getEmbedLocale())
      if (!text) return null
      return (
        <p className="text-xs leading-relaxed text-muted-foreground" dir="auto">
          {text}
        </p>
      )
    },

    // ── MultiSelectList (from multi-select-step.tsx) ─────
    MultiSelectList: ({ props, emit }: BaseComponentProps) => {
      const p = props as { options: Array<{ value: string; label: string; description?: string | null }> }
      const options = p.options || []
      const [selected, setSelected] = useState<string[]>([])

      const handleToggle = (optionValue: string) => {
        setSelected((prev) => {
          const nextSelected = prev.includes(optionValue)
            ? prev.filter((v) => v !== optionValue)
            : [...prev, optionValue]
          emit('change', { data: nextSelected })
          return nextSelected
        })
      }

      return (
        <div className="space-y-3">
          <input type="hidden" name="selectedOptions" value={JSON.stringify(selected)} />
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors hover:bg-muted/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            >
              <Checkbox checked={selected.includes(option.value)} onCheckedChange={() => handleToggle(option.value)} className="mt-0.5" />
              <div className="flex-1">
                <Label className="cursor-pointer font-medium">{option.label}</Label>
                {option.description && <p className="text-sm text-muted-foreground mt-1">{option.description}</p>}
              </div>
            </label>
          ))}
        </div>
      )
    },
  },

  actions: {
    sendMessage: async () => {
      // Bridged via ActionProvider at usage site
    },
    submitStepData: async () => {
      // Bridged via ActionProvider at usage site
    },
    uploadFiles: async () => {
      // Bridged via ActionProvider at usage site
    },
    submitFormData: async () => {
      // Bridged via ActionProvider at usage site (SpecActionBridge)
    },
  },
})
